import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { Loader2, Trash2, ArrowLeft, Plus } from "lucide-react";
import { Icons } from "../components/icons";
import useOrganisation from "../hooks/useOrganisation";
import CreateOrganisationModal from "../components/Organisation/CreateOrganisationModal";
import AddProjectQuestionModal from "../components/Organisation/AddProjectQuestionModal";
import InviteClientModal from "../components/Organisation/InviteClientModal";
import AddClientQuestionModal from "../components/Organisation/AddClientQuestionModal";
import DeleteOrganisationModal from "../components/Organisation/DeleteOrganisationModal";

// Small inline icons for the organisation page.
const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7 sm:h-8 sm:w-8" aria-hidden="true"><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01" /></svg>
);
const UserPlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>
);
const FolderIcon = ({ className = "h-7 w-7" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
);
const LinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
);
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
);

const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

// ─── Organisation list ──────────────────────────────────────────────
function OrganisationList({ onOpenCreate }) {
  const navigate = useNavigate();
  const {
    organisations,
    isFetchingOrganisations,
    organisationsError,
    loadOrganisations,
  } = useOrganisation();

  useEffect(() => {
    loadOrganisations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-5 rounded-[18px] border border-[#EAECF0] bg-white p-5 shadow-[0_1px_3px_rgba(16,24,40,0.05)] sm:p-7">
      <div className="mb-6 flex items-center justify-between gap-3 border-b border-[#F2F4F7] pb-5">
        <div>
          <h2 className="text-[20px] font-semibold text-[#101828]">
            Organisations
          </h2>
          <p className="mt-0.5 text-[13px] text-[#667085]">
            Group client projects and contacts
          </p>
        </div>
        {/* Once at least one organisation exists the action lives here, in the
            top-right corner, rather than centred in the empty state. */}
        {organisations.length > 0 && (
          <button
            onClick={onOpenCreate}
            className="flex h-11 items-center gap-2 rounded-[12px] bg-[#4F46E5] px-4 text-[14px] font-semibold text-white shadow-[0_4px_12px_rgba(79,70,229,0.3)] hover:bg-[#4338CA]"
          >
            <Icons.Organisation className="w-4 h-4" />
            New Organisation
          </button>
        )}
      </div>

      {organisationsError && (
        <p className="text-sm text-red-500 text-center py-4">
          {organisationsError}
        </p>
      )}

      {isFetchingOrganisations ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-[#4253C7]" size={28} />
        </div>
      ) : organisations.length === 0 ? (
        <EmptyOrganisations onOpenCreate={onOpenCreate} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {organisations.map((org) => (
            <button
              key={org._id}
              onClick={() => navigate(`/dashboard/organisation/${org._id}`)}
              className="rounded-[16px] border border-[#EAECF0] bg-white p-5 text-left transition-colors hover:border-[#C7CBF5] hover:shadow-[0_4px_12px_rgba(16,24,40,0.06)]"
            >
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-gradient-to-br from-[#6366F1] to-[#4338CA] text-white">
                  <BuildingIcon />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-semibold text-[#101828]">
                    {org.name}
                  </p>
                  <p className="truncate text-[12px] text-[#98A2B3]">
                    {org.description || "No description"}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 border-t border-[#F2F4F7] pt-3 text-[12px] text-[#667085]">
                <span className="rounded-full bg-[#F2F4F7] px-2 py-[2px]">
                  {org.projectCount} project{org.projectCount === 1 ? "" : "s"}
                </span>
                <span className="rounded-full bg-[#F2F4F7] px-2 py-[2px]">
                  {org.clientCount} client{org.clientCount === 1 ? "" : "s"}
                </span>
                <span className="ml-auto text-[#98A2B3]">{fmt(org.createdAt)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Centred icon + message + action. Once an organisation exists this is
// replaced by the list, and the action moves to the top-right corner.
function EmptyOrganisations({ onOpenCreate }) {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-4 rounded-[16px] border border-dashed border-[#D0D5DD] py-16">
      <span className="flex h-16 w-16 items-center justify-center rounded-[16px] border border-[#E0E3FF] bg-[#F5F6FF] text-[#4F46E5]">
        <BuildingIcon />
      </span>
      <p className="text-[15px] font-medium text-[#475467]">No organisation formed</p>
      <button
        onClick={onOpenCreate}
        className="flex h-11 items-center gap-2 rounded-[12px] bg-[#4F46E5] px-5 text-[14px] font-semibold text-white shadow-[0_4px_12px_rgba(79,70,229,0.3)] hover:bg-[#4338CA]"
      >
        <Icons.Organisation className="w-4 h-4" />
        Form New Organisation
      </button>
    </div>
  );
}

// ─── Organisation detail ────────────────────────────────────────────
function OrganisationDetail({ organisationId }) {
  const navigate = useNavigate();
  const { openCreateProject } = useOutletContext();
  const {
    selectedOrganisation,
    isFetchingOrganisation,
    organisationError,
    unassignedProjects,
    isFetchingUnassigned,
    isAddingProject,
    addProjectError,
    isInvitingClient,
    inviteClientError,
    isDeletingOrganisation,
    deleteOrganisationError,
    availableClients,
    isFetchingAvailableClients,
    isAddingExistingClient,
    addExistingClientError,
    loadOrganisation,
    loadUnassignedProjects,
    loadAvailableClients,
    attachProject,
    detachProject,
    addClient,
    sendClientInvite,
    removeOrganisation,
    resetInvite,
    clearErrors,
  } = useOrganisation();

  const [showAddProject, setShowAddProject] = useState(false);
  // Which step the project chooser opens on: "ask" normally, "existing" when
  // coming from the empty state's "Link Existing Project" card.
  const [addProjectMode, setAddProjectMode] = useState("ask");
  const [showAddClient, setShowAddClient] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showInviteSent, setShowInviteSent] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    loadOrganisation(organisationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  // Also wired straight to onClick, where the argument is the click event —
  // only a string is treated as a mode.
  const openAddProject = (mode) => {
    clearErrors();
    loadUnassignedProjects();
    setAddProjectMode(typeof mode === "string" ? mode : "ask");
    setShowAddProject(true);
  };

  const handleAddExisting = async (projectId) => {
    const result = await attachProject(organisationId, projectId);
    if (result.meta.requestStatus === "fulfilled") {
      setShowAddProject(false);
      loadOrganisation(organisationId);
    }
  };

  const openAddClient = () => {
    clearErrors();
    loadAvailableClients(organisationId);
    setShowAddClient(true);
  };

  const handleAddExistingClient = async (client) => {
    const result = await addClient(organisationId, client._id);
    if (result.meta.requestStatus === "fulfilled") {
      setShowAddClient(false);
      loadOrganisation(organisationId);
    }
  };

  const handleInvite = async (payload) => {
    const result = await sendClientInvite(organisationId, payload);
    if (result.meta.requestStatus === "fulfilled") {
      setShowInvite(false);
      setShowInviteSent(true);
      resetInvite();
      loadOrganisation(organisationId);
      return true;
    }
    return false;
  };

  const handleDelete = async (mode) => {
    const result = await removeOrganisation(organisationId, mode);
    if (result.meta.requestStatus === "fulfilled") {
      setConfirmDelete(null);
      navigate("/dashboard/organisation");
    }
  };

  if (isFetchingOrganisation) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-[#4253C7]" size={28} />
      </div>
    );
  }

  if (organisationError || !selectedOrganisation) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 mt-5 text-center">
        <p className="text-gray-400 text-sm">
          {organisationError || "Organisation not found."}
        </p>
        <button
          onClick={() => navigate("/dashboard/organisation")}
          className="text-[#4253C7] text-sm underline mt-4"
        >
          Back to organisations
        </button>
      </div>
    );
  }

  const org = selectedOrganisation;
  const projects = org.projects || [];
  const clients = org.clients || [];

  return (
    <div className="mt-5">
      <button
        onClick={() => navigate("/dashboard/organisation")}
        className="mb-4 flex items-center gap-2 text-[14px] font-medium text-[#4F46E5] hover:underline"
      >
        <ArrowLeft size={16} />
        Back to All Organisations
      </button>

      {/* Header — Add Client and Add Project always sit top-right, so clients
          can be added before any project exists. */}
      <div className="rounded-[18px] border border-[#EAECF0] bg-gradient-to-r from-white via-white to-[#F5F6FF] p-5 shadow-[0_1px_3px_rgba(16,24,40,0.05)] sm:p-7">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-4 sm:gap-5">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[16px] bg-gradient-to-br from-[#6366F1] to-[#4338CA] text-white shadow-[0_8px_20px_rgba(79,70,229,0.35)] sm:h-[72px] sm:w-[72px]">
              <BuildingIcon />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <h2 className="truncate text-[22px] font-semibold text-[#101828] sm:text-[28px]">
                  {org.name}
                </h2>
                {!org.isDisabled && (
                  <span className="flex items-center gap-1.5 rounded-full border border-[#ABEFC6] bg-[#ECFDF3] px-2.5 py-[3px] text-[12px] font-medium text-[#067647]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#17B26A]" />
                    Active Workspace
                  </span>
                )}
                <span className="flex items-center gap-1.5 rounded-full border border-[#E4E7EC] bg-[#F9FAFB] px-2.5 py-[3px] text-[12px] font-medium text-[#475467]">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                  {clients.length} Client{clients.length === 1 ? "" : "s"}
                </span>
              </div>
              <p className="mt-1.5 max-w-[640px] text-[14px] leading-[22px] text-[#667085]">
                {org.description || "No description"}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <button
              onClick={openAddClient}
              className="flex h-11 items-center gap-2 rounded-[12px] border border-[#D0D5DD] bg-white px-4 text-[14px] font-medium text-[#344054] shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-[#F9FAFB]"
            >
              <UserPlusIcon />
              Add Client
            </button>
            <button
              onClick={openAddProject}
              className="flex h-11 items-center gap-2 rounded-[12px] bg-[#4F46E5] px-4 text-[14px] font-semibold text-white shadow-[0_4px_12px_rgba(79,70,229,0.3)] hover:bg-[#4338CA]"
            >
              <Plus size={16} />
              Add Project
            </button>
            <button
              onClick={() => setConfirmDelete(org)}
              className="flex h-11 w-11 items-center justify-center rounded-[12px] border border-[#E4E7EC] bg-white text-[#98A2B3] hover:border-[#FDA29B] hover:text-[#D92D20]"
              aria-label="Delete organisation"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Projects */}
      <div className="mt-5 rounded-[18px] border border-[#EAECF0] bg-white p-5 shadow-[0_1px_3px_rgba(16,24,40,0.05)] sm:p-7">
        <div className="mb-6 flex items-center justify-between gap-3 border-b border-[#F2F4F7] pb-5">
          <div className="flex items-center gap-3">
            <h3 className="text-[20px] font-semibold text-[#101828]">Projects</h3>
            <span className="rounded-full border border-[#E4E7EC] bg-[#F9FAFB] px-2.5 py-[2px] text-[12px] font-medium text-[#475467]">
              {projects.length} Project{projects.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        {projects.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-[16px] border border-[#E0E3FF] bg-[#F5F6FF] text-[#4F46E5]">
              <FolderIcon />
            </span>
            <p className="mt-5 text-[17px] font-semibold text-[#101828]">
              No projects in this organisation yet
            </p>
            <p className="mt-1.5 max-w-[520px] text-[14px] leading-[22px] text-[#667085]">
              Kickstart delivery by creating a brand-new project or linking an
              existing one from your portfolio.
            </p>

            {/* The two paths the chooser offers, laid out as cards. Each goes
                to exactly the same place the chooser would. */}
            <div className="mt-8 grid w-full max-w-[860px] grid-cols-1 gap-4 text-left md:grid-cols-2">
              <button
                onClick={() => openCreateProject(organisationId)}
                className="group rounded-[16px] border border-[#EAECF0] bg-white p-6 transition-colors hover:border-[#C7CBF5] hover:shadow-[0_4px_12px_rgba(16,24,40,0.06)]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-[10px] bg-[#EEF0FF] text-[#4F46E5]">
                  <Plus size={20} />
                </span>
                <p className="mt-5 text-[16px] font-semibold text-[#101828]">
                  Create New Project
                </p>
                <p className="mt-1 text-[13px] leading-[21px] text-[#667085]">
                  Set up the project, its team members and dates from scratch
                  with the usual setup wizard.
                </p>
                <span className="mt-5 flex items-center gap-1 border-t border-[#F2F4F7] pt-4 text-[13px] font-medium text-[#4F46E5]">
                  Start Setup
                  <ChevronRightIcon />
                </span>
              </button>

              <button
                onClick={() => openAddProject("existing")}
                className="group rounded-[16px] border border-[#EAECF0] bg-white p-6 transition-colors hover:border-[#C7CBF5] hover:shadow-[0_4px_12px_rgba(16,24,40,0.06)]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-[#EAECF0] bg-[#F9FAFB] text-[#344054]">
                  <LinkIcon />
                </span>
                <p className="mt-5 text-[16px] font-semibold text-[#101828]">
                  Link Existing Project
                </p>
                <p className="mt-1 text-[13px] leading-[21px] text-[#667085]">
                  Move a project that isn't in any organisation yet into{" "}
                  {org.name}.
                </p>
                <span className="mt-5 flex items-center gap-1 border-t border-[#F2F4F7] pt-4 text-[13px] font-medium text-[#344054]">
                  Browse Portfolio
                  <ChevronRightIcon />
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {projects.map((p) => (
              <div
                key={p._id}
                className="rounded-[14px] border border-[#EAECF0] bg-white p-5 transition-colors hover:border-[#C7CBF5] hover:shadow-[0_4px_12px_rgba(16,24,40,0.06)]"
              >
                {/* Clicking opens the project exactly as the Projects page does */}
                <button
                  onClick={() => navigate(`/dashboard/projects/${p._id}`)}
                  className="w-full text-left"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#F5F6FF] text-[#4F46E5]">
                      <FolderIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-[15px] font-semibold text-[#101828]">
                        {p.name}
                      </p>
                      <p className="truncate text-[12px] text-[#98A2B3]">
                        {p.description || "No description"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-[12px]">
                    {p.status && (
                      <span className="rounded-full bg-[#F2F4F7] px-2 py-[2px] capitalize text-[#475467]">
                        {p.status}
                      </span>
                    )}
                    {p.priority && (
                      <span className="rounded-full bg-[#F2F4F7] px-2 py-[2px] capitalize text-[#475467]">
                        {p.priority}
                      </span>
                    )}
                    <span className="ml-auto font-semibold text-[#344054]">
                      {p.progress ?? 0}%
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#F2F4F7]">
                    <div
                      className="h-full rounded-full bg-[#4F46E5]"
                      style={{ width: `${p.progress ?? 0}%` }}
                    />
                  </div>
                </button>
                <button
                  onClick={() => detachProject(organisationId, p._id)}
                  className="mt-4 text-[12px] text-[#98A2B3] hover:text-[#D92D20] hover:underline"
                >
                  Remove from organisation
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clients */}
      <div className="mt-5 rounded-[18px] border border-[#EAECF0] bg-white p-5 shadow-[0_1px_3px_rgba(16,24,40,0.05)] sm:p-7">
        <div className="mb-5 flex items-center justify-between gap-3 border-b border-[#F2F4F7] pb-5">
          <div className="flex items-center gap-3">
            <h3 className="text-[20px] font-semibold text-[#101828]">
              Clients &amp; Stakeholders
            </h3>
            <span className="rounded-full border border-[#E4E7EC] bg-[#F9FAFB] px-2.5 py-[2px] text-[12px] font-medium text-[#475467]">
              {clients.length} Client{clients.length === 1 ? "" : "s"}
            </span>
          </div>
          <button
            onClick={openAddClient}
            className="flex items-center gap-1.5 rounded-[8px] border border-[#EAECF0] bg-[#F9FAFB] px-3 py-2 text-[13px] font-medium text-[#344054] hover:bg-[#F2F4F7]"
          >
            <Plus size={15} />
            Invite Client
          </button>
        </div>

        {clients.length === 0 ? (
          <p className="py-10 text-center text-[14px] text-[#98A2B3]">
            No clients invited yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-[13px]">
              <thead>
                <tr className="border-b border-[#F2F4F7] text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-[#98A2B3]">
                  <th className="pb-3">Client</th>
                  <th className="pb-3">Description</th>
                  <th className="pb-3">Invited On</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr
                    key={c._id || c.user?._id}
                    className="border-b border-[#F9FAFB] last:border-b-0"
                  >
                    <td className="py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EEF0FF] text-[11px] font-semibold text-[#4F46E5]">
                          {(c.user?.name || c.user?.email || "?")
                            .slice(0, 2)
                            .toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-[#101828]">
                            {c.user?.name || "—"}
                          </p>
                          <p className="truncate text-[11px] text-[#98A2B3]">
                            {c.user?.email || "—"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="text-[#667085]">{c.user?.description || "—"}</td>
                    <td className="text-[#667085]">{fmt(c.invitedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddProjectQuestionModal
        open={showAddProject}
        initialMode={addProjectMode}
        onClose={() => setShowAddProject(false)}
        onCreateNew={() => openCreateProject(organisationId)}
        onAddExisting={handleAddExisting}
        unassignedProjects={unassignedProjects}
        isLoadingProjects={isFetchingUnassigned}
        isAdding={isAddingProject}
        error={addProjectError}
      />

      <AddClientQuestionModal
        open={showAddClient}
        onClose={() => setShowAddClient(false)}
        onInviteNew={() => {
          clearErrors();
          setShowInvite(true);
        }}
        onAddExisting={handleAddExistingClient}
        availableClients={availableClients}
        isLoadingClients={isFetchingAvailableClients}
        isAdding={isAddingExistingClient}
        error={addExistingClientError}
      />

      <InviteClientModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onInvite={handleInvite}
        isSaving={isInvitingClient}
        error={inviteClientError}
      />

      {showInviteSent && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-50"
            onClick={() => setShowInviteSent(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl z-50 w-[460px] max-w-[calc(100vw-32px)] p-8 shadow-2xl text-center">
            <div className="w-[72px] h-[72px] rounded-full bg-[#F0F0FF] flex items-center justify-center mx-auto mb-4">
              <Icons.Envelope />
            </div>
            <h3 className="text-[18px] font-semibold text-black">
              Invite Sent!
            </h3>
            <p className="text-[13px] text-gray-500 mt-2">
              The client will appear in Settings &gt; Resources with a{" "}
              <span className="text-[#7C4A10] font-medium">PENDING</span> status
              until they complete setup.
            </p>
            <button
              onClick={() => setShowInviteSent(false)}
              className="bg-[#4253C7] text-white w-full h-[40px] rounded-lg text-sm mt-6"
            >
              Done
            </button>
          </div>
        </>
      )}

      <DeleteOrganisationModal
        organisation={confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        isDeleting={isDeletingOrganisation}
        error={deleteOrganisationError}
      />
    </div>
  );
}

// ─── Page shell ─────────────────────────────────────────────────────
const Organisation = () => {
  const { orgRef } = useParams();
  const {
    isCreatingOrganisation,
    createOrganisationError,
    makeOrganisation,
    loadOrganisations,
    clearErrors,
  } = useOrganisation();

  const [showCreate, setShowCreate] = useState(false);

  const handleCreate = async (payload) => {
    const result = await makeOrganisation(payload);
    if (result.meta.requestStatus === "fulfilled") {
      setShowCreate(false);
      loadOrganisations();
      return true;
    }
    return false;
  };

  return (
    <div className="w-[95%] mx-auto pb-10">
      {orgRef ? (
        <OrganisationDetail organisationId={orgRef} />
      ) : (
        <OrganisationList
          onOpenCreate={() => {
            clearErrors();
            setShowCreate(true);
          }}
        />
      )}

      <CreateOrganisationModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={handleCreate}
        isSaving={isCreatingOrganisation}
        error={createOrganisationError}
      />
    </div>
  );
};

export default Organisation;
